/**
 * Clean-up for vocabulary text that reached the database with generation
 * artifacts attached.
 *
 * A handful of `saved_vocabulary` rows were written with a leading status label
 * baked into the text itself, e.g.
 *
 *   "SAVED - having keen mental discernment"
 *   "SAVED example sagacious"
 *   "SAVED edited definition - LASTING for a very short time (edited, enriched)"
 *
 * The label belongs to the save/edit flow, not to the definition, so a learner
 * reading the card sees pipeline noise instead of the meaning. No current code
 * path emits it — it is stale data — but stripping it at the edge keeps the
 * reading surface correct without waiting for a backfill.
 *
 * These helpers are deliberately **conservative**: they only remove a prefix
 * when it matches the exact label shapes below, and they never rewrite the
 * body. A definition that legitimately starts with the word "saved" (as in
 * "saved goods", "a saved life") is left completely alone because the pattern
 * requires the status word plus a role word and a separator.
 */

/**
 * `SAVED` / `SAVED edited`, then **either** a role word (`definition`,
 * `example`, `ipa`) **or** a dash/colon separator.
 *
 * Requiring one of those two is what keeps real English safe: without it the
 * fully-optional version also matched "saved goods" and turned it into "goods".
 */
const LEADING_LABEL =
    /^\s*SAVED\b(?:\s+(?:edited|original|previous))?(?:\s+(?:definition|example|ipa)\b(?:\s*[-–—:]\s*)?|\s*[-–—:]\s*)/i;

/** Trailing `(edited, enriched)`-style provenance marker. */
const TRAILING_MARKER = /\s*[([]\s*(edited|enriched)(?:\s*,\s*(edited|enriched))*\s*[)\]]\s*$/i;

/**
 * Strip a leading save/edit label from a definition or example.
 *
 * Returns the input unchanged when nothing matches.
 */
export const stripGenerationLabel = (text?: string | null): string => {
    if (typeof text !== "string") return "";
    const trimmed = text.trim();
    if (!trimmed) return "";
    // Guard against stripping a real word: the label must be the whole prefix,
    // not merely the first word of a sentence.
    const stripped = trimmed.replace(LEADING_LABEL, "");
    // If stripping consumed essentially the whole string there was no real
    // content behind it, so keep the original rather than showing nothing.
    return stripped.trim().length > 0 ? stripped.trim() : trimmed;
};

/**
 * Clean a definition for display: drop the leading label and any trailing
 * provenance marker.
 */
export const sanitizeDefinition = (text?: string | null): string => {
    const withoutLabel = stripGenerationLabel(text);
    return withoutLabel.replace(TRAILING_MARKER, "").trim();
};

/** Clean an example sentence for display. */
export const sanitizeExample = (text?: string | null): string => {
    const withoutLabel = stripGenerationLabel(text);
    return withoutLabel.replace(TRAILING_MARKER, "").trim();
};